// src/auth/RequireAuth.jsx
import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import { getLoginRequest, getRedirectStartPage } from "../msal";
import { ENV, tbGetJson, ensureTeamBridgeCookieSession, startTeamBridgeCookieSessionRefresh } from "../api/client";
import { buildUrl } from "../utils/url";
//import { proxyJwtSmokeTest } from "../api/client";

/* ===== CONFIG ===== */
const ABSOLUTE_SESSION_MIN = Number(ENV.ABSOLUTE_SESSION_MIN ?? 480);
const IDLE_TIMEOUT_MIN = Number(ENV.IDLE_TIMEOUT_MIN ?? 60);
const IDLE_WARN_BEFORE_SEC = 60;

const LOOKUP_SCHEMA_VER = "v2"; // response shape changed (wrapper + response)
const ENABLE_LOOKUP_CACHE = true;

/* ===== KNOWN ROLES ===== */
const KNOWN_ROLES = new Set([
    "Nominator",
    "Employee Supplier Admin",
    "Employee Sponsor",
    "DTV OPS Admin",
    "CP Channel Delegate",
    "FTE Nominator",
    "Read Only",
]);

/* ===== HELPERS ===== */
const SS = sessionStorage;
const K = {
    kicked: "tb_login_kicked",
    name: "tb_name",
    email: "tb_email",
    role: "tb_role",
    roleRaw: "tb_role_raw",
    vendorId: "tb_vendorId",
    dtvid: "tb_dtvid",
    attuid: "tb_attuid",
    personType: "tb_personType",
    dtvRoleName: "tb_dtvRoleName",
    supplierName: "tb_supplierName",
    directReports: "tb_directReports",
    isFte: "tb_isFte",
    lookup: "tb_lookup",
    lookupAt: "tb_lookup_at",
    lookupVer: "tb_lookup_ver",
    hingeList: "tb_hinge_person_list",
    loginAt: "tb_login_at",
    lastActivity: "tb_last_activity",
    groups: "tb_groups",

    // used for lookup dedupe across remounts
    lookupKey: "tb_lookup_key",

    // written by RoleSwitcher when user manually picks a role;
    // bootstrap honours this so the chosen role survives a reload
    roleSelected: "tb_role_user_selected",
};

const RETURN_TO_KEY = "tb_return_to";
const minutes = (n) => n * 60 * 1000;

/* ===== ROLE PRIORITY (SSO groups) ===== */
const ROLE_PRIORITY = [
    "DTV OPS Admin",
    "Employee Supplier Admin",
    "Employee Sponsor",
    "Nominator",
    "FTE Nominator",   // included so SSO group IDs can also grant this role
    "Read Only",
];

/* ============================================================
   SINGLE-FLIGHT LOOKUP DEDUPE (survives React StrictMode remount)
   ============================================================ */
const LOOKUP_SINGLEFLIGHT = {
    key: "",
    promise: null,
};

/* ============================================================
   BOOTSTRAP DEFERRAL (prevents StrictMode "test mount" fetch)
   - In React 18 StrictMode (dev), effects mount/unmount/mount.
   - We schedule bootstrap on a timer and cancel on cleanup, so
     the first "test mount" never hits the network.
   ============================================================ */
const BOOTSTRAP_TIMER_DELAY_MS = 0;

/* ===== STRING SANITIZER ===== */
function cleanStr(v) {
    const s = String(v ?? "").trim();
    if (!s) return "";
    const lower = s.toLowerCase();
    if (lower === "null" || lower === "undefined") return "";
    return s;
}

function roleFromGroups(claims) {
    const rawGroups = claims?.groups || claims?.["user.groups"] || [];
    const groups = Array.isArray(rawGroups)
        ? rawGroups.map((g) => String(g).toLowerCase())
        : [];
    if (!groups.length) return "";

    for (const role of ROLE_PRIORITY) {
        const ids = (ENV.ROLE_GROUPS?.[role] || []).map((s) => String(s).toLowerCase());
        if (ids.length && groups.some((g) => ids.includes(g))) return role;
    }
    return "";
}

/**
 * Returns ALL roles the user qualifies for via SSO group membership.
 * Unlike roleFromGroups() which stops at the first match, this checks
 * every entry in ROLE_PRIORITY and collects all matches.
 */
function allRolesFromGroups(claims) {
    const rawGroups = claims?.groups || claims?.["user.groups"] || [];
    const groups = Array.isArray(rawGroups)
        ? rawGroups.map((g) => String(g).toLowerCase())
        : [];
    if (!groups.length) return [];

    return ROLE_PRIORITY.filter((role) => {
        const ids = (ENV.ROLE_GROUPS?.[role] || []).map((s) => String(s).toLowerCase());
        return ids.length > 0 && groups.some((g) => ids.includes(g));
    });
}


function isChannelDelegateFromProfile(prof) {
    const v =
        prof?.isChannelDelegate ??
        prof?.isChannelDelegateIndicator ??
        prof?.isChannelDelegateFlag ??
        prof?.isChannelDelegateYn ??
        prof?.isChannelDelegateYN ??
        prof?.is_cp_delegate ??
        prof?.cpDelegate;

    const s = String(v ?? "").trim().toLowerCase();
    return v === true || s === "true" || s === "y" || s === "1";
}

function isFteFromProfile(prof) {
    const pt = cleanStr(prof?.personType || prof?.person_type || "");
    return /^employee$/i.test(pt);
}

/**
 * NEW lookup response shape:
 * { requestId, timing, response: { ...profileFields } }
 * Normalize to the inner response object.
 */
function unwrapLookup(payload) {
    if (!payload || typeof payload !== "object") return null;

    // already looks like a profile object
    if (payload.personType || payload.person_type || payload.primaryEmail || payload.attuid) {
        return payload;
    }

    const inner = payload.response;
    if (inner && typeof inner === "object") return inner;

    return null;
}

function persistProfileToSession(account) {
    if (!account) return;
    const claims = account.idTokenClaims || {};

    const name = cleanStr(claims["full name"] || account.name || claims.name || "");
    const email = cleanStr(
        claims.email ||
        claims.emailaddress ||
        account.username ||
        claims.preferred_username ||
        ""
    );

    const dtvid = cleanStr(
        claims["dtvId"] ||
        claims["attuid/attuid"] ||
        claims.attuid ||
        claims.attuid_dtv ||
        claims.dtvId ||
        claims["dtvId"] ||
        ""
    );

    const rawGroups = claims?.groups || [];
    const roleFromGrp = roleFromGroups(claims);
    const existingRole = cleanStr(SS.getItem(K.role) || "");
    const userSelectedRole = cleanStr(SS.getItem(K.roleSelected) || "");

    // NOTE: you currently hard-force role for testing. Leaving as-is.
    const finalRole =
        //"FTE Nominator";
        //"Nominator";
        // "Employee Sponsor";
        //"CP Channel Delegate";
        //    "Employee Supplier Admin"; 
        (userSelectedRole && KNOWN_ROLES.has(userSelectedRole))
            ? userSelectedRole
            : KNOWN_ROLES.has(roleFromGrp)
                ? roleFromGrp
                : (KNOWN_ROLES.has(existingRole) ? existingRole : "");

    try {
        if (name) SS.setItem(K.name, name);
        else SS.removeItem(K.name);

        if (email) SS.setItem(K.email, email);
        else SS.removeItem(K.email);

        if (Array.isArray(rawGroups)) SS.setItem(K.groups, JSON.stringify(rawGroups));

        SS.setItem(K.roleRaw, roleFromGrp || "");
        if (finalRole) SS.setItem(K.role, finalRole);
        else SS.removeItem(K.role);

        if (dtvid) SS.setItem(K.dtvid, dtvid);
        else SS.removeItem(K.dtvid);

        const vendorId = cleanStr(claims["extension_vendorId"] || claims.vendorId);
        if (vendorId) SS.setItem(K.vendorId, vendorId);
    } catch { }
}

function readRedirectError() {
    if (!window.location.hash.includes("error=")) return null;
    const p = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    return {
        code: p.get("error") || "",
        description: decodeURIComponent(p.get("error_description") || ""),
    };
}

function touchActivity() {
    try {
        SS.setItem(K.lastActivity, String(Date.now()));
    } catch { }
}

function getCachedLookup(identityKey) {
    try {
        if (!ENABLE_LOOKUP_CACHE) return null;

        const ver = SS.getItem(K.lookupVer);
        if (ver && ver !== LOOKUP_SCHEMA_VER) return null;

        // ensure cache belongs to same identity
        const cachedKey = cleanStr(SS.getItem(K.lookupKey) || "");
        if (identityKey && cachedKey && cachedKey !== identityKey) return null;

        const raw = SS.getItem(K.lookup);
        if (!raw) return null;

        const payload = JSON.parse(raw);
        const prof = unwrapLookup(payload);
        if (!prof || typeof prof !== "object") return null;

        return prof;
    } catch {
        return null;
    }
}

function putCachedLookup(identityKey, payload) {
    try {
        SS.setItem(K.lookup, JSON.stringify(payload || {}));
        SS.setItem(K.lookupAt, String(Date.now()));
        SS.setItem(K.lookupVer, LOOKUP_SCHEMA_VER);
        if (identityKey) SS.setItem(K.lookupKey, identityKey);
    } catch { }
}

/**
 * Resolve the lookup base URL:
 * - Prefer ENV.LOOKUP_URL
 * - Else use ENV.TB_HINGE_LOOKUP_URL
 */
function resolveLookupBase() {
    const a = cleanStr(ENV.LOOKUP_URL || "");
    if (a) return a.replace(/\/$/, "");

    const b = cleanStr(ENV.TB_HINGE_LOOKUP_URL || "");
    if (b) return b.replace(/\/$/, "");

    return "";
}

/**
 * SINGLE-FLIGHT FETCH:
 * - If a lookup for the same identity is already in-flight, await it
 * - Else start it
 */
async function fetchLookupSingleFlight({ lookupUrl, identityKey }) {
    if (LOOKUP_SINGLEFLIGHT.promise && LOOKUP_SINGLEFLIGHT.key === identityKey) {
        return LOOKUP_SINGLEFLIGHT.promise;
    }

    LOOKUP_SINGLEFLIGHT.key = identityKey;
    LOOKUP_SINGLEFLIGHT.promise = (async () => {
        try {
            return await tbGetJson(lookupUrl);
        } finally {
            LOOKUP_SINGLEFLIGHT.promise = null;
            LOOKUP_SINGLEFLIGHT.key = "";
        }
    })();

    return LOOKUP_SINGLEFLIGHT.promise;
}

/* ===== LOOKUP ERROR NORMALIZER ===== */
function normalizeLookupError(e) {
    const msg = cleanStr(e?.message || "");
    const name = cleanStr(e?.name || "");
    const status =
        e?.status ||
        e?.response?.status ||
        e?.response?.statusCode ||
        e?.data?.statusCode ||
        null;

    const aborted =
        name === "AbortError" || /aborted|abort|canceled|cancelled|ERR_ABORTED/i.test(msg);

    return {
        aborted,
        status: Number.isFinite(Number(status)) ? Number(status) : null,
        message: msg || "Lookup failed",
    };
}

/**
 * We want lookup to run ONLY when identity inputs are stable.
 * - Prefer dtvId/email from session
 * - Also read from account claims as a fallback
 * - IMPORTANT: avoid using DEFAULT_DTVID immediately on the first paint,
 *   because that can cause a "first lookup" using default, then a second lookup
 *   once real dtvId is available.
 */
function getIdentityCandidates(account) {
    const fromSession = {
        dtvId: cleanStr(SS.getItem(K.dtvid) || ""),
        email: cleanStr(SS.getItem(K.email) || ""),
    };

    const claims = account?.idTokenClaims || {};
    const fromClaims = {
        email: cleanStr(
            claims.email ||
            claims.emailaddress ||
            account?.username ||
            claims.preferred_username ||
            ""
        ),
        dtvId: cleanStr(
            claims["dtvId"] ||
            claims["attuid/attuid"] ||
            claims.attuid ||
            claims.attuid_dtv ||
            claims.dtvId ||
            ""
        ),
    };

    return { fromSession, fromClaims };
}

/**
 * Wait up to maxWaitMs for dtvId/email to appear (session or claims).
 * This prevents premature lookup that later gets "replaced" by a second lookup.
 */
async function waitForStableIdentity(account, maxWaitMs = 1500, stepMs = 75) {
    const t0 = Date.now();
    while (Date.now() - t0 < maxWaitMs) {
        const { fromSession, fromClaims } = getIdentityCandidates(account);
        const dtvId = cleanStr(fromSession.dtvId || fromClaims.dtvId);
        const email = cleanStr(fromSession.email || fromClaims.email);

        if (dtvId || email) {
            return cleanStr(dtvId || email);
        }
        // small delay
        await new Promise((r) => setTimeout(r, stepMs));
    }

    // last chance: use whatever is present now
    const { fromSession, fromClaims } = getIdentityCandidates(account);
    const dtvId = cleanStr(fromSession.dtvId || fromClaims.dtvId);
    const email = cleanStr(fromSession.email || fromClaims.email);

    // if still empty, only then fallback to DEFAULT_DTVID (your existing behavior)
    return cleanStr(dtvId || email || ENV.DEFAULT_DTVID || "");
}

export default function RequireAuth({ children }) {
    const { instance, accounts, inProgress } = useMsal();
    const nav = useNavigate();
    const account = accounts?.[0] || null;
    const isAuthed = !!account;

    // Establish TeamBridge cookie-session once we have an OIDC/JWT token from MSAL.
    // This calls: https://api.dev.directv.com/dtvcs-teambridge-api/api/login (Authorization: Bearer <token>)
    // and relies on browser-managed cookies for subsequent API calls.
    const tbCookieInitRef = useRef(false);
    useEffect(() => {
        if (!account) return;
        if (inProgress !== InteractionStatus.None) return;
        if (tbCookieInitRef.current) return;
        tbCookieInitRef.current = true;

        (async () => {
            try {
                await ensureTeamBridgeCookieSession(false);
                startTeamBridgeCookieSessionRefresh();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn("[TB] cookie login init failed:", e?.message || e);
                // allow retries on next render if needed
                tbCookieInitRef.current = false;
            }
        })();
    }, [account, inProgress]);

    const isUnauthorizedPath =
        typeof window !== "undefined" && window.location.pathname === "/unauthorized";
    const isAuthCallbackPath =
        typeof window !== "undefined" && window.location.pathname === "/auth/callback";

    const kickedRef = useRef(SS.getItem(K.kicked) === "1");
    const [redirErr, setRedirErr] = useState(null);
    const [showIdleWarn, setShowIdleWarn] = useState(false);
    const [idleSecondsLeft, setIdleSecondsLeft] = useState(IDLE_WARN_BEFORE_SEC);

    // Lookup failure modal
    const [lookupFailed, setLookupFailed] = useState(false);
    const [lookupFailDetails, setLookupFailDetails] = useState("");

    useEffect(() => {
        const err = readRedirectError();
        if (err) setRedirErr(err);
    }, []);

    // One-shot watchdog: unauthenticated and no redirect starts -> kick login after ~1s
    useEffect(() => {
        if (isAuthed) return;
        if (isUnauthorizedPath || isAuthCallbackPath) return;

        const t = setTimeout(() => {
            if (!isAuthed && inProgress === InteractionStatus.None && !kickedRef.current) {
                try {
                    const href = (window.location?.href || "/").split("#")[0];
                    SS.setItem(RETURN_TO_KEY, href);
                } catch { }

                kickedRef.current = true;
                SS.setItem(K.kicked, "1");
                const req = { ...getLoginRequest(), redirectStartPage: getRedirectStartPage() };
                instance.loginRedirect(req).catch(() => {
                    kickedRef.current = false;
                    SS.removeItem(K.kicked);
                });
            }
        }, 1000);

        return () => clearTimeout(t);
    }, [isAuthed, inProgress, instance, isUnauthorizedPath, isAuthCallbackPath]);

    // Normal login kick
    useEffect(() => {
        if (redirErr) return;

        if (
            !isAuthed &&
            inProgress === InteractionStatus.None &&
            !kickedRef.current &&
            !isUnauthorizedPath &&
            !isAuthCallbackPath
        ) {
            try {
                const href = (window.location?.href || "/").split("#")[0];
                SS.setItem(RETURN_TO_KEY, href);
            } catch { }

            kickedRef.current = true;
            SS.setItem(K.kicked, "1");

            const req = { ...getLoginRequest(), redirectStartPage: getRedirectStartPage() };
            instance.loginRedirect(req).catch(() => {
                kickedRef.current = false;
                SS.removeItem(K.kicked);
            });
        }
    }, [isAuthed, inProgress, instance, redirErr, isUnauthorizedPath, isAuthCallbackPath]);

    // After sign-in, hard-restore deep link if needed
    useEffect(() => {
        if (!isAuthed || inProgress !== InteractionStatus.None) return;
        try {
            const wanted = SS.getItem(RETURN_TO_KEY);
            if (wanted) {
                const now = (window.location?.href || "").split("#")[0];
                if (wanted !== now) {
                    SS.removeItem(RETURN_TO_KEY);
                    window.location.replace(wanted);
                    return;
                }
                SS.removeItem(RETURN_TO_KEY);
            }
        } catch { }
    }, [isAuthed, inProgress]);

    // Save basics
    useEffect(() => {
        if (isAuthed && inProgress === InteractionStatus.None) {
            SS.removeItem(K.kicked);
            persistProfileToSession(account);
            if (!SS.getItem(K.loginAt)) SS.setItem(K.loginAt, String(Date.now()));
            if (!SS.getItem(K.lastActivity)) SS.setItem(K.lastActivity, String(Date.now()));
        }
    }, [isAuthed, inProgress, account]);

    // Claims update
    useEffect(() => {
        if (account) persistProfileToSession(account);
    }, [account?.idToken, account?.idTokenClaims, account]);

    // Bootstrap + TB-proxied lookup (RUN ONCE when deps are stable)
    useEffect(() => {
        if (!isAuthed || inProgress !== InteractionStatus.None) return;
        if (isUnauthorizedPath) return;

        // If deep link restore is pending, DO NOT start lookup (navigation aborts fetch).
        try {
            const wanted = SS.getItem(RETURN_TO_KEY);
            const now = (window.location?.href || "").split("#")[0];
            if (wanted && wanted !== now) return;
        } catch { }

        let canceled = false;
        let timer = null;

        const run = async () => {
            try {
                setLookupFailed(false);
                setLookupFailDetails("");
                /*
                try {
                    const proxyResp = await proxyJwtSmokeTest();
                    console.log("[AUTH] Proxy JWT test success:", proxyResp);
                } catch (e) {
                    console.error("[AUTH] Proxy JWT test failed:", e?.message || e);
                    setLookupFailed(true);
                    setLookupFailDetails(
                        e?.status
                            ? `Proxy rejected JWT (HTTP ${e.status})`
                            : "Proxy JWT validation failed"
                    );
                    return;
                } */

                // Wait for dtvId/email to actually be available (prevents premature lookup)
                const identityForLookup = await waitForStableIdentity(account, 1500, 75);
                if (canceled) return;

                if (!identityForLookup) {
                    nav("/unauthorized", { replace: true });
                    return;
                }

                // Cache first (scoped to identity)
                let prof = getCachedLookup(identityForLookup);

                if (!prof) {
                    const lookupBase = resolveLookupBase();
                    if (!lookupBase) {
                        nav("/unauthorized", { replace: true });
                        return;
                    }

                    const lookupUrl = buildUrl(lookupBase, { dtvId: identityForLookup });

                    let payload = null;
                    try {
                        payload = await fetchLookupSingleFlight({
                            lookupUrl,
                            identityKey: identityForLookup,
                        });
                    } catch (e) {
                        const info = normalizeLookupError(e);

                        // If aborted/canceled (StrictMode teardown / navigation), suppress modal.
                        if (!info.aborted) {
                            setLookupFailed(true);
                            setLookupFailDetails(
                                info.status
                                    ? `HTTP ${info.status}${info.message ? ` — ${info.message}` : ""}`
                                    : info.message || "No response from lookup API"
                            );
                        }
                        return;
                    }

                    if (canceled) return;

                    if (!payload) {
                        setLookupFailed(true);
                        setLookupFailDetails("No response from lookup API. Please logout and login again.");
                        return;
                    }

                    putCachedLookup(identityForLookup, payload);

                    prof = unwrapLookup(payload);
                    if (!prof) {
                        setLookupFailed(true);
                        setLookupFailDetails(
                            "Lookup response is invalid/empty. Please logout and login again."
                        );
                        return;
                    }
                }

                // Persist profile bits
                const directReports = Array.isArray(prof.directReports) ? prof.directReports : [];

                const personType = cleanStr(prof.personType || prof.person_type || "");
                const dtvRoleName = cleanStr(prof.dtvRoleName || prof["dtvRoleName "] || "");
                const supplierName = cleanStr(prof.supplierName || "");

                const firstName = cleanStr(prof.firstName);
                const lastName = cleanStr(prof.lastName);
                const fullName = cleanStr([firstName, lastName].filter(Boolean).join(" "));
                const primaryEmail = cleanStr(prof.primaryEmail || prof.primary_email || "");

                const attuid = cleanStr(prof.attuid ?? prof.attUid ?? prof.ATTUID ?? prof["attuid"] ?? "");
                const dtvAnchorId = cleanStr(prof.dtvAnchorId ?? prof.dtvId ?? prof.dtv_id ?? "");

                SS.setItem(K.supplierName, supplierName);
                if (supplierName && !cleanStr(SS.getItem(K.vendorId))) SS.setItem(K.vendorId, supplierName);

                if (fullName && !cleanStr(SS.getItem(K.name))) SS.setItem(K.name, fullName);
                if (primaryEmail && !cleanStr(SS.getItem(K.email))) SS.setItem(K.email, primaryEmail);

                if (dtvAnchorId && !cleanStr(SS.getItem(K.dtvid))) SS.setItem(K.dtvid, dtvAnchorId);

                SS.setItem(K.personType, personType);
                SS.setItem(K.dtvRoleName, dtvRoleName);
                SS.setItem(K.directReports, JSON.stringify(directReports));
                SS.setItem(K.isFte, isFteFromProfile(prof) ? "1" : "0");

                if (attuid) SS.setItem(K.attuid, attuid);
                else SS.removeItem(K.attuid);

                // ── Role resolution ──────────────────────────────────────────
                // Build the FULL set of roles this user is entitled to from ALL
                // sources, then pick the highest-priority one as the active role.
                //
                // Sources checked independently (union, no source wins over another):
                //   • SSO groups  → ALL matched group IDs via allRolesFromGroups()
                //   • Hinge flag  → CP Channel Delegate if isChannelDelegate === true
                //   • Hinge type  → FTE Nominator if personType === "Employee"
                //
                // FTE Nominator specifically: granted if SSO group matches OR Hinge
                // says Employee — either is sufficient, both shows only one entry.
                // ─────────────────────────────────────────────────────────────

                const roleSet = new Set();

                // 1. All SSO-group-derived roles (reads live claims, not tb_role)
                //    This replaces reading tb_role which only ever held ONE role.
                const claims = account?.idTokenClaims || {};
                for (const r of allRolesFromGroups(claims)) {
                    if (KNOWN_ROLES.has(r)) roleSet.add(r);
                }

                // 2. CP Channel Delegate from Hinge profile flag
                if (isChannelDelegateFromProfile(prof)) {
                    roleSet.add("CP Channel Delegate");
                }

                // 3. FTE Nominator from Hinge (SSO path already handled above)
                //    Using Set means even if both sources match, only one entry added.
                if (isFteFromProfile(prof)) {
                    roleSet.add("FTE Nominator");
                }

                // Convert to ordered array for RoleSwitcher dropdown
                // Order here defines priority: index 0 = default active role
                const ORDERED_ROLES = [
                    "DTV OPS Admin",
                    "Employee Supplier Admin",
                    "Employee Sponsor",
                    "Nominator",
                    "FTE Nominator",
                    "CP Channel Delegate",
                    "Read Only",
                ];
                const availableRoles = ORDERED_ROLES.filter((r) => roleSet.has(r));

                // Pick effective/active role:
                // If the user manually switched roles (tb_role_user_selected is set)
                // AND that role is still in their entitlement list, honour it.
                // Otherwise fall back to the highest-priority role (index 0).
                const userSelected = cleanStr(SS.getItem(K.roleSelected) || "");
                const effectiveRole =
                    (userSelected && availableRoles.includes(userSelected))
                        ? userSelected
                        : (availableRoles[0] || "");

                if (effectiveRole && KNOWN_ROLES.has(effectiveRole)) {
                    SS.setItem(K.role, effectiveRole);
                    SS.setItem("tb_available_roles", JSON.stringify(availableRoles));
                    // Keep tb_role_user_selected so subsequent reloads (idle refresh etc.)
                    // also land on the right role. It is only cleared on full logout.
                } else {
                    SS.removeItem(K.role);
                    SS.removeItem("tb_available_roles");
                    SS.removeItem(K.roleSelected);
                    nav("/unauthorized", { replace: true });
                    return;
                }
            } catch (e) {
                console.error("[AUTH] bootstrap/lookup failed:", e?.message || e);
                setLookupFailed(true);
                setLookupFailDetails(cleanStr(e?.message) || "Unexpected error. Please logout and login again.");
            }
        };

        // IMPORTANT: defer so StrictMode "test mount" cleanup cancels this timer (no network call).
        timer = setTimeout(() => {
            if (!canceled) run();
        }, BOOTSTRAP_TIMER_DELAY_MS);

        return () => {
            canceled = true;
            if (timer) clearTimeout(timer);
        };
    }, [isAuthed, inProgress, isUnauthorizedPath, nav, account]);

    // Idle/absolute timers (unchanged)
    useEffect(() => {
        if (!isAuthed || isUnauthorizedPath) return;

        const evs = ["click", "keydown", "mousemove", "scroll", "touchstart", "visibilitychange"];
        const onActivity = () => {
            if (document.visibilityState === "visible" && !showIdleWarn) touchActivity();
        };
        evs.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

        const tick = setInterval(async () => {
            try {
                const loginAt = Number(SS.getItem(K.loginAt) || "0");
                const last = Number(SS.getItem(K.lastActivity) || "0");
                const idleMs = Date.now() - (last || 0);
                const absMs = Date.now() - (loginAt || 0);

                const idleLimitMs = Number.isFinite(IDLE_TIMEOUT_MIN) ? minutes(IDLE_TIMEOUT_MIN) : null;
                const absLimitMs = Number.isFinite(ABSOLUTE_SESSION_MIN) ? minutes(ABSOLUTE_SESSION_MIN) : null;
//comment for time left
                // const fmt = (ms) => {
                //     const s = Math.max(0, Math.ceil(ms / 1000));
                //     return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
                // };
                // const idleLeftMs = idleLimitMs != null ? idleLimitMs - idleMs : null;
                // const absLeftMs = absLimitMs != null ? absLimitMs - absMs : null;
                // console.log(
                //     `[AUTH] time left → idle: ${idleLeftMs != null ? fmt(idleLeftMs) : "n/a"} | absolute: ${absLeftMs != null ? fmt(absLeftMs) : "n/a"}`
                // );

                if (absLimitMs != null && absMs >= absLimitMs) {
                    await forceReauth(instance);
                    return;
                }
                if (idleLimitMs != null) {
                    const msRemaining = idleLimitMs - idleMs;
                    if (msRemaining <= 0) {
                        await forceReauth(instance);
                        return;
                    }
                    if (msRemaining <= IDLE_WARN_BEFORE_SEC * 1000) {
                        setShowIdleWarn(true);
                        setIdleSecondsLeft(Math.max(1, Math.ceil(msRemaining / 1000)));
                    }
                }
            } catch (e) {
                console.warn("[AUTH] idle/absolute check failed:", e);
            }
        }, 1000);

        return () => {
            clearInterval(tick);
            evs.forEach((ev) => window.removeEventListener(ev, onActivity));
        };
    }, [isAuthed, instance, showIdleWarn, isUnauthorizedPath]);

    const handleStaySignedIn = () => {
        touchActivity();
        setShowIdleWarn(false);
    };

    let content;
    if (!isAuthed) {
        if (redirErr) {
            content = (
                <div className="main">
                    <div className="card" style={{ maxWidth: 680 }}>
                        <h3>Sign-in failed</h3>
                        <div style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                            {redirErr.code}: {redirErr.description}
                        </div>
                        <button
                            className="btn"
                            style={{ marginTop: 12 }}
                            onClick={() => {
                                setRedirErr(null);
                                SS.removeItem(K.kicked);
                                window.location.hash = "";
                            }}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            );
        } else {
            content = (
                <div className="main">
                    <div className="card">
                        {inProgress !== InteractionStatus.None ? "Signing you in ..." : "Redirecting to sign in ..."}
                    </div>
                </div>
            );
        }
    } else {
        content = children;
    }

    return (
        <>
            {content}

            {/* Lookup failed modal */}
            {isAuthed && lookupFailed && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h2>Hinge lookup failed</h2>
                        <p style={{ fontSize: 14, marginBottom: 8, color: "#111827" }}>
                            We could not load your profile details from Hinge lookup. Please logout and login again.
                        </p>
                        {lookupFailDetails && (
                            <p style={{ fontSize: 13, marginBottom: 16, color: "#4b5563", whiteSpace: "pre-wrap" }}>
                                {lookupFailDetails}
                            </p>
                        )}
                        <div className="modal-buttons" style={{ gap: 8 }}>
                            <button type="button" className="primary" onClick={() => forceReauth(instance)}>
                                Logout &amp; Login Again
                            </button>
                            <button type="button" className="primary" onClick={() => setLookupFailed(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Existing idle warning modal */}
            {isAuthed && showIdleWarn && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h2>Are you still there?</h2>
                        <p style={{ fontSize: 14, marginBottom: 8, color: "#111827" }}>
                            You&apos;ve been inactive for a while. For security, you&apos;ll be signed out soon.
                        </p>
                        <p style={{ fontSize: 13, marginBottom: 16, color: "#4b5563" }}>
                            You&apos;ll be signed out in <strong>{idleSecondsLeft}</strong>{" "}
                            second{idleSecondsLeft === 1 ? "" : "s"} unless you choose to stay signed in.
                        </p>
                        <div className="modal-buttons" style={{ gap: 8 }}>
                            <button type="button" className="primary" onClick={() => forceReauth(instance)}>
                                Sign out now
                            </button>
                            <button type="button" className="primary" onClick={handleStaySignedIn}>
                                Stay signed in
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export async function forceReauth(instance) {
    if (typeof window !== "undefined" && window.location.pathname === "/unauthorized") return;
    try {
        [
            "tb_login_kicked",
            "tb_name",
            "tb_email",
            "tb_role",
            "tb_role_raw",
            "tb_available_roles",
            "tb_role_user_selected",
            "tb_vendorId",
            "tb_dtvid",
            "tb_attuid",
            "tb_personType",
            "tb_dtvRoleName",
            "tb_supplierName",
            "tb_directReports",
            "tb_isFte",
            "tb_hinge_person_list",
            "tb_login_at",
            "tb_last_activity",
            "tb_lookup",
            "tb_lookup_at",
            "tb_lookup_ver",
            "tb_groups",
            "tb_lookup_key",
        ].forEach((k) => sessionStorage.removeItem(k));
    } catch { }
    await instance.logoutRedirect().catch(() => { });
}
